import { useEffect, useRef, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Phone, Navigation, Clock, Truck, Package, MapPin, User, Building2, Warehouse, Star, KeyRound } from "lucide-react";

// Pickup icon - orange circle
const pickupIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:36px;height:36px;background:#FF6B00;border-radius:50%;border:3px solid white;box-shadow:0 2px 12px rgba(255,107,0,0.4);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3" fill="white"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

// Delivery icon - green pin
const deliveryIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:36px;height:36px;background:#16a34a;border-radius:50%;border:3px solid white;box-shadow:0 2px 12px rgba(22,163,74,0.4);display:flex;align-items:center;justify-content:center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3" fill="white"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

// Hub icons — Main (solid orange square) vs Micro (white square w/ orange border)
const mainHubIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:30px;height:30px;background:#FF6B00;border-radius:8px;border:3px solid white;box-shadow:0 2px 10px rgba(255,107,0,0.35);display:flex;align-items:center;justify-content:center">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M3 21h18M5 21V8l7-5 7 5v13M9 21V12h6v9"/></svg>
  </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const microHubIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:26px;height:26px;background:white;border-radius:8px;border:2px solid #FF6B00;box-shadow:0 2px 8px rgba(255,107,0,0.25);display:flex;align-items:center;justify-content:center">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FF6B00" stroke-width="2.5"><path d="M3 9h18v12H3zM6 9V5a3 3 0 016 0v4M12 9V5a3 3 0 016 0v4"/></svg>
  </div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

// Agent icon - pulsing blue truck
const agentIcon = new L.DivIcon({
  className: "",
  html: `<div style="position:relative;width:44px;height:44px">
    <div style="position:absolute;inset:0;background:rgba(37,99,235,0.15);border-radius:50%;animation:agentPulse 2s ease-in-out infinite"></div>
    <div style="position:absolute;top:6px;left:6px;width:32px;height:32px;background:#2563eb;border-radius:50%;border:3px solid white;box-shadow:0 2px 16px rgba(37,99,235,0.5);display:flex;align-items:center;justify-content:center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
    </div>
  </div>`,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

// Inject pulse animation CSS
if (typeof document !== "undefined" && !document.getElementById("agent-pulse-css")) {
  const style = document.createElement("style");
  style.id = "agent-pulse-css";
  style.textContent = `@keyframes agentPulse { 0%,100%{transform:scale(1);opacity:0.4} 50%{transform:scale(1.5);opacity:0} }`;
  document.head.appendChild(style);
}

function MapUpdater({ agentPos, deliveryPos, routePolyline }) {
  const map = useMap();
  const lastAgent = useRef(null);

  useEffect(() => {
    if (!agentPos) return;
    // Only refit if agent moved significantly
    if (lastAgent.current) {
      const dist = map.distance(
        [lastAgent.current.lat, lastAgent.current.lng],
        [agentPos.lat, agentPos.lng]
      );
      if (dist < 50) return; // Skip tiny movements
    }
    lastAgent.current = agentPos;

    const points = [[agentPos.lat, agentPos.lng]];
    if (deliveryPos) points.push([deliveryPos.lat, deliveryPos.lng]);
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 16, animate: true });
  }, [agentPos, deliveryPos, map]);

  return null;
}

function InitBounds({ points }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (points.length >= 2 && !fitted.current) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      fitted.current = true;
    }
  }, [points, map]);
  return null;
}

export default function LiveTrackingMap({
  pickup,
  delivery,
  agentLocation,
  agentName,
  agentPhone,
  status,
  estimatedDays,
  className = "",
  // Swiggy/Uber-style enhancements (Phase: live-tracking, Feb 2026):
  hubJourney,        // { origin_micro_hub, origin_main_hub, destination_main_hub, destination_micro_hub, intra_hub, intra_city }
  pickupAgent,       // { name, phone (only after assignment), vehicle_type, rating, last_location }
  deliveryAgent,
  deliveryOtp,       // 4-digit OTP shown to customer; reminder in delivery agent popup
  demoMode = true,   // if true and no live agentLocation, interpolate position along the route
}) {
  const [routeCoords, setRouteCoords] = useState([]);
  const [agentToDestRoute, setAgentToDestRoute] = useState([]);
  const [liveEta, setLiveEta] = useState(estimatedDays);
  const [simulatedAgentPos, setSimulatedAgentPos] = useState(null);
  const prevAgent = useRef(null);

  // Fetch route from OSRM
  const fetchRoute = useCallback(async (from, to) => {
    try {
      const resp = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`
      );
      const data = await resp.json();
      if (data.code === "Ok" && data.routes?.[0]) {
        const coords = data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        const duration = Math.round(data.routes[0].duration / 60);
        return { coords, duration };
      }
    } catch { /* network / OSRM down — fall through to straight-line */ }
    return null;
  }, []);

  // Fetch full route (pickup → delivery) on mount
  useEffect(() => {
    if (!pickup?.lat || !delivery?.lat) return;
    fetchRoute(pickup, delivery).then((r) => {
      if (r) setRouteCoords(r.coords);
    });
  }, [pickup, delivery, fetchRoute]);

  // Fetch agent → destination route when agent moves
  useEffect(() => {
    if (!agentLocation?.lat || !pickup?.lat || !delivery?.lat) return;
    const prev = prevAgent.current;
    if (prev && Math.abs(prev.lat - agentLocation.lat) < 0.0001 && Math.abs(prev.lng - agentLocation.lng) < 0.0001) return;
    prevAgent.current = agentLocation;

    const dest = status === "assigned" ? pickup : delivery;
    fetchRoute(agentLocation, dest).then((r) => {
      if (r) {
        setAgentToDestRoute(r.coords);
        if (r.duration) {
          const days = r.duration <= 50 * 60 ? 1 : r.duration <= 500 * 60 ? 2 : 3;
          setLiveEta(days);
        }
      }
    });
  }, [agentLocation, status, pickup, delivery, fetchRoute]);

  // Demo-mode interpolation — when an agent is assigned but no live
  // GPS feed is available (Twilio/Agent app not wired), animate the
  // cursor smoothly along the OSRM polyline so customers still get
  // the "moving on the map" experience. Clearly badged as "Simulated"
  // in the bottom card.
  const isActive = ["assigned", "pickup_assigned", "picked_up", "in_transit", "out_for_delivery"].includes(status);
  const usingSimulation = demoMode && isActive && !agentLocation?.lat && routeCoords.length > 1;
  useEffect(() => {
    if (!usingSimulation) {
      setSimulatedAgentPos(null);
      return undefined;
    }
    let i = 0;
    setSimulatedAgentPos({ lat: routeCoords[0][0], lng: routeCoords[0][1] });
    const handle = setInterval(() => {
      i = (i + 1) % routeCoords.length;
      const [lat, lng] = routeCoords[i];
      setSimulatedAgentPos({ lat, lng });
    }, 1800);
    return () => clearInterval(handle);
  }, [usingSimulation, routeCoords]);

  const effectiveAgentLocation = agentLocation?.lat ? agentLocation : (usingSimulation ? simulatedAgentPos : null);

  if (!pickup?.lat || !delivery?.lat) return null;

  // Build the list of hub nodes (with coords) for marker rendering.
  const hubNodes = [];
  if (hubJourney) {
    const slots = [
      ["origin_micro_hub", hubJourney.origin_micro_hub],
      ["origin_main_hub", hubJourney.origin_main_hub],
      ["destination_main_hub", hubJourney.destination_main_hub],
      ["destination_micro_hub", hubJourney.destination_micro_hub],
    ];
    for (const [slot, node] of slots) {
      if (!node || node.lat == null || node.lng == null) continue;
      hubNodes.push({ ...node, slot });
    }
  }
  const linehaulLeg = (
    hubJourney
    && hubJourney.origin_main_hub?.lat != null
    && hubJourney.destination_main_hub?.lat != null
    && hubJourney.origin_main_hub.hub_id !== hubJourney.destination_main_hub.hub_id
  ) ? [
    [hubJourney.origin_main_hub.lat, hubJourney.origin_main_hub.lng],
    [hubJourney.destination_main_hub.lat, hubJourney.destination_main_hub.lng],
  ] : null;

  const center = [(pickup.lat + delivery.lat) / 2, (pickup.lng + delivery.lng) / 2];
  const allPoints = [pickup, delivery, ...hubNodes];
  if (effectiveAgentLocation?.lat) allPoints.push(effectiveAgentLocation);

  // Decide which agent is "the one to surface" right now.
  const isPickupPhase = ["assigned", "pickup_assigned"].includes(status);
  const isDeliveryPhase = ["out_for_delivery", "in_transit"].includes(status);
  const liveAgent = isDeliveryPhase ? deliveryAgent : (isPickupPhase ? pickupAgent : (pickupAgent || deliveryAgent));
  const displayName = liveAgent?.name || agentName || "Agent";
  const displayPhone = liveAgent?.phone || agentPhone;
  const displayVehicle = liveAgent?.vehicle_type;
  const displayRating = liveAgent?.rating;

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm ${className}`} data-testid="live-tracking-map">
      {/* Map */}
      <MapContainer center={center} zoom={13} scrollWheelZoom={true} zoomControl={false} style={{ height: "100%", width: "100%", minHeight: 350 }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <InitBounds points={allPoints} />
        {effectiveAgentLocation?.lat && <MapUpdater agentPos={effectiveAgentLocation} deliveryPos={status === "assigned" ? pickup : delivery} />}

        {/* Full route polyline (faded — "upcoming") */}
        {routeCoords.length > 0 && (
          <Polyline positions={routeCoords} pathOptions={{ color: "#94a3b8", weight: 4, opacity: 0.3 }} />
        )}

        {/* Agent → destination route (highlighted — "completed/in-progress") */}
        {agentToDestRoute.length > 0 && isActive && (
          <Polyline positions={agentToDestRoute} pathOptions={{ color: "#2563eb", weight: 4, opacity: 0.8 }} />
        )}

        {/* Fallback straight lines if no OSRM route */}
        {routeCoords.length === 0 && (
          <Polyline positions={[[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]]} pathOptions={{ color: "#FF6B00", weight: 3, dashArray: "8 6", opacity: 0.5 }} />
        )}

        {/* Linehaul leg between two Main Hubs (different cities) */}
        {linehaulLeg && (
          <Polyline
            positions={linehaulLeg}
            pathOptions={{ color: "#FF6B00", weight: 3, dashArray: "10 6", opacity: 0.7 }}
          />
        )}

        <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon}>
          <Popup>
            <div style={{ minWidth: 160 }}>
              <strong style={{ color: "#FF6B00" }}>Pickup Location</strong>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>The package starts its journey here.</div>
            </div>
          </Popup>
        </Marker>

        <Marker position={[delivery.lat, delivery.lng]} icon={deliveryIcon}>
          <Popup>
            <div style={{ minWidth: 160 }}>
              <strong style={{ color: "#16a34a" }}>Delivery Location</strong>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Final stop for your package.</div>
            </div>
          </Popup>
        </Marker>

        {/* Hub markers — 4 nodes, deduped client-side via .slot key */}
        {hubNodes.map((node) => {
          const isMain = node.hub_type === "main";
          return (
            <Marker
              key={node.slot}
              position={[node.lat, node.lng]}
              icon={isMain ? mainHubIcon : microHubIcon}
            >
              <Popup>
                <div style={{ minWidth: 180 }} data-testid={`hub-popup-${node.slot}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                    {isMain ? "🏢" : "🏬"}
                    <strong style={{ color: "#FF6B00" }}>{node.name}</strong>
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
                    {isMain ? "Main Hub" : "Micro Hub"} · {node.city}
                  </div>
                  {node.address && <div style={{ fontSize: 12, color: "#475569", marginTop: 6 }}>{node.address}</div>}
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
                    {node.slot.startsWith("origin")
                      ? "Your package starts/sorts here."
                      : "Your package arrives here for last-mile delivery."}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Live agent marker */}
        {effectiveAgentLocation?.lat && (
          <Marker position={[effectiveAgentLocation.lat, effectiveAgentLocation.lng]} icon={agentIcon}>
            <Popup>
              <div style={{ minWidth: 200 }} data-testid="agent-popup">
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <strong>{displayName}</strong>
                  {displayRating && (
                    <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700 }}>★ {displayRating}</span>
                  )}
                </div>
                {displayVehicle && (
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                    {String(displayVehicle).replace(/_/g, " ")}
                  </div>
                )}
                {usingSimulation && (
                  <div style={{ fontSize: 10, color: "#f59e0b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>
                    Simulated movement
                  </div>
                )}
                {isDeliveryPhase && deliveryOtp && (
                  <div style={{ fontSize: 12, color: "#16a34a", marginTop: 6, fontWeight: 600 }}>
                    🔐 Share OTP <span style={{ fontFamily: "monospace" }}>{deliveryOtp}</span> on delivery
                  </div>
                )}
                {displayPhone && (
                  <a href={`tel:${displayPhone}`} style={{ display: "inline-block", marginTop: 6, color: "#16a34a", fontWeight: 600 }}>
                    📞 Call {displayPhone}
                  </a>
                )}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Swiggy-style bottom card */}
      {isActive && (displayName !== "Agent" || effectiveAgentLocation?.lat) && (
        <div className="absolute bottom-0 left-0 right-0 z-[1000]" data-testid="agent-info-card">
          <div className="bg-white/95 backdrop-blur-xl border-t border-slate-200 rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-5 py-4">
            <div className="flex items-center gap-3.5">
              {/* Agent avatar */}
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shrink-0 shadow-md shadow-blue-600/30">
                <User className="h-6 w-6 text-white" />
              </div>

              {/* Agent info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-bold text-slate-900 truncate">{displayName}</p>
                  {displayRating && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-700">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      {displayRating}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold border ${
                    usingSimulation
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-green-50 text-green-700 border-green-200"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${usingSimulation ? "bg-amber-500" : "bg-green-500"}`} />
                    {usingSimulation ? "DEMO" : "LIVE"}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {status === "assigned" && "Heading to pickup location"}
                  {status === "pickup_assigned" && "Pickup agent heading to collect package"}
                  {status === "picked_up" && "Package collected, on the way"}
                  {status === "in_transit" && "Delivering to your doorstep"}
                  {status === "out_for_delivery" && "Delivery agent on the way to you"}
                </p>
                {displayVehicle && (
                  <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{String(displayVehicle).replace(/_/g, " ")}</p>
                )}
              </div>

              {/* ETA + OTP cue */}
              <div className="text-right shrink-0">
                <div className="flex items-center gap-1 justify-end">
                  <Clock className="h-3.5 w-3.5 text-[#FF6B00]" />
                  <span className="text-lg font-bold text-slate-900">{liveEta || estimatedDays || "--"}</span>
                </div>
                <p className="text-[10px] text-slate-400 font-medium">{(liveEta || estimatedDays) === 1 ? "day" : "days"}</p>
                {isDeliveryPhase && deliveryOtp && (
                  <p className="text-[10px] font-bold text-emerald-700 mt-1 inline-flex items-center gap-0.5" data-testid="delivery-otp-reminder">
                    <KeyRound className="h-3 w-3" /> OTP {deliveryOtp}
                  </p>
                )}
              </div>

              {/* Call button — only when phone is exposed (post-assignment) */}
              {displayPhone && (
                <a
                  href={`tel:${displayPhone}`}
                  className="w-11 h-11 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shrink-0 shadow-md shadow-green-500/30 transition-all active:scale-95"
                  data-testid="agent-call-btn"
                >
                  <Phone className="h-5 w-5 text-white" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delivered overlay */}
      {status === "delivered" && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] bg-green-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2" data-testid="delivered-badge">
          <Package className="h-4 w-4" />
          <span className="text-sm font-bold">Delivered Successfully</span>
        </div>
      )}
    </div>
  );
}
